/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {vec2, vec3, mat4} from "gl-matrix";
import {ITheosetup} from "./total_station_interface";
import Tube from "../../elements/tube";
import Point from "../../elements/point";
import RenderDocument from "js_renderer";
import IScene from "../../scene_interface";
import IDocument from "../../structural_elements/document_interface";

export default
class TotalStation {

    protected _sceneAPI: IScene;
    protected _sceneDocument: IDocument;
    protected _renderDocument: RenderDocument;

    // scene elements
    private _gps: Point;
    private _disc: any;
    private _tripod: any;
    private _head: any;
    private _instrument: Point;
    private _ros: Point[];
    private _tsVisible: boolean;
    private _rosVisible: boolean;
    private _headRotation: number;
    private _theoPosScene: vec3;

    constructor(aRenderDocument: RenderDocument) {
        this._renderDocument = aRenderDocument;
        this._sceneAPI = aRenderDocument.getSceneAPI();
        this._sceneDocument = this._sceneAPI.getDocument();

        this._disc = null;
        this._tripod = null;
        this._head = null;
        this._instrument = null;
        this._ros = [];
        this._headRotation = 0;

        this._tsVisible = false;
        this._rosVisible = false;
        this._theoPosScene = vec3.create();
    }

    public update(aSetupData: ITheosetup) {
        this.clear();
        this.updateTotalStation(aSetupData);

        this.updateRos(aSetupData);
    }

    public showTotalStation(aState: boolean) {
        this._tsVisible = aState;
        this.showDisc(this._tsVisible);
        this.showTripod(this._tsVisible);
        this.showHead(this._tsVisible);
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public showRos(aState: boolean) {
        this._rosVisible = aState;

        for (let ro of this._ros) {
            ro.setVisibility(aState);
        }
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public clear() {
        this._headRotation = 0;
        this._theoPosScene = vec3.create();

        if (this._disc) {
            // this._disc.remove();
            this._sceneAPI.getDocument().removeAsset(this._disc);
            this._disc = null;
        }

        if (this._instrument) {
            this._instrument.remove();
            this._instrument = null;
        }

        if (this._tripod) {
            // this._tripod.remove();
            this._sceneAPI.getDocument().removeAsset(this._tripod);
            this._tripod = null;
        }

        if (this._head) {
            // this._tripod.remove();
            this._sceneAPI.getDocument().removeAsset(this._head);
            this._head = null;
        }

        for (let ro of this._ros) {
            ro.remove();
        }
        this._ros = [];

        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public updateHead(aSetupData: any) {
        if (this._head) {
            let rotationMat = mat4.create();
            this._headRotation -= aSetupData;
            mat4.translate(rotationMat, rotationMat, this._theoPosScene);
            mat4.rotateY(rotationMat, rotationMat, this._headRotation);
            let invertTranslation = vec3.fromValues(-this._theoPosScene[0], -this._theoPosScene[1], -this._theoPosScene[2]);
            mat4.translate(rotationMat, rotationMat, invertTranslation);
            this._head.setModelTransformation(rotationMat);
            this._sceneAPI.viewModeModule.scheduleRedraw();
        }
    }

    protected updateTotalStation(aSetupData: ITheosetup) {

        let theoPos: vec3 = vec3.fromValues(Number(aSetupData.theo.theoP.x), Number(aSetupData.theo.theoP.y), Number(aSetupData.theo.theoP.z));
        let instrumentHeight: number = Number(aSetupData.theo.theoPHeight);

        // demo...
        // let theoPos: vec3 = vec3.fromValues(1, 1, 0);
        // let instrumentHeight = 1;

        this._theoPosScene = this._sceneDocument.transformToCadworkCS(theoPos, true);

        // TS models
        this.createDisc(this._theoPosScene);
        this.createTripod(this._theoPosScene);
        this.createHead(this._theoPosScene, instrumentHeight);

        this.showTotalStation(true);
    }

    protected updateRos(aSetupData: ITheosetup) {
        let ros = aSetupData.ros;
        for (let ro of ros) {

            let roPos: vec3 = vec3.fromValues(Number(ro.roPosition.x), Number(ro.roPosition.y), Number(ro.roPosition.z));
            let roPosScene = this._sceneDocument.transformToCadworkCS(roPos, true);

            let roHeight: number = Number(ro.roHeight);

            let roElement = this.createRo(roPosScene, roHeight);
            this._ros.push(roElement);
        }
        this.showRos(true);
    }

    private createDisc(aPosition: vec3) {

        let trMatrix = mat4.create();
        mat4.translate(trMatrix, trMatrix, aPosition);
        this._disc = this._sceneDocument.addAsset(
            "https://www.lexoview.com/models/BimBamAssets/ts_disc/geometryDescription.json",
            "https://www.lexoview.com/models/BimBamAssets/ts_disc/modelDescription.json",
            false,
            trMatrix
            );
        this._disc.setSelectionAllowed(false);

        // this._disc = this._sceneDocument.createAPIElement(Point.TypeName) as Point;
        // this._sceneDocument.attachElementToInternalModel(this._disc);
        //
        // this._disc.extendProperties({
        //     minPointSize: 16.0,
        //     maxPointSize: 16.0,
        //     glyphPosition: [96 / Point.texture_size[0], 0 / Point.texture_size[1]],
        //     glyphSize: [16.0 / Point.texture_size[0], 16.0 / Point.texture_size[1]],
        //     alwaysOnTop: false,
        //     pickable: true,
        //     pinPoint: [0.0, 0.0]
        // });

        // this._disc.setPosition(aPosition);
        // this._disc.draw(true);
    }

    private createTripod(aPosition: vec3) {
        let trMatrix = mat4.create();
        mat4.translate(trMatrix, trMatrix, aPosition);
        this._tripod = this._sceneDocument.addAsset(
            "https://www.lexoview.com/models/BimBamAssets/ts-tripod/geometryDescription.json",
            "https://www.lexoview.com/models/BimBamAssets/ts-tripod/modelDescription.json",
            false,
            trMatrix
        );
        this._tripod.setSelectionAllowed(false);
    }

    private createHead(aPosition: vec3, aHeight: number) {
        // if (this._instrument) {
        //     delete this._instrument;
        // }
        //
        // this._instrument = this._sceneDocument.createAPIElement(Point.TypeName) as Point;
        // this._sceneDocument.attachElementToInternalModel(this._instrument);
        //
        // this._instrument.extendProperties({
        //     minPointSize: 16.0,
        //     maxPointSize: 16.0,
        //     glyphPosition: [64 / Point.texture_size[0], 0 / Point.texture_size[1]],
        //     glyphSize: [16.0 / Point.texture_size[0], 16.0 / Point.texture_size[1]],
        //     alwaysOnTop: false,
        //     pickable: true,
        //     pinPoint: [0.0, 0.0]
        // });
        //
        // let instrumentPos: vec3 = vec3.create();
        // vec3.add(instrumentPos, aPosition, new vec3.fromValues(0, aHeight, 0));
        // this._instrument.setPosition(instrumentPos);
        // this._instrument.draw(true);

        let trMatrix = mat4.create();
        mat4.translate(trMatrix, trMatrix, aPosition);
        this._head = this._sceneDocument.addAsset(
            "https://www.lexoview.com/models/BimBamAssets/ts-head/geometryDescription.json",
            "https://www.lexoview.com/models/BimBamAssets/ts-head/modelDescription.json",
            false,
            trMatrix
        );
        this._head.setSelectionAllowed(false);
    }

    private createRo(aPosition: vec3, aHeight: number): Point {
        let roElement: Point = this._sceneDocument.createAPIElement(Point.TypeName) as Point;
        this._sceneDocument.attachElementToInternalModel(roElement);

        let position: vec3 = vec3.create();

        roElement.extendProperties({
            minPointSize: 16.0,
            maxPointSize: 16.0,
            glyphPosition: [112 / Point.texture_size[0], 0 / Point.texture_size[1]],
            glyphSize: [16.0 / Point.texture_size[0], 16.0 / Point.texture_size[1]],
            alwaysOnTop: false,
            pickable: true,
            pinPoint: [0.0, 0.0]
        });

        let roTop: vec3 = vec3.create();
        vec3.add(roTop, aPosition, new vec3.fromValues(0, aHeight, 0));
        roElement.setPosition(aPosition); // roTop
        roElement.draw(true);

        return roElement;
    }

    private showDisc(aState: boolean) {
        if (this._disc) {
            this._disc.setVisibility(aState);
        }
    }

    private showTripod(aState: boolean) {
        if (this._tripod) {
            this._tripod.setVisibility(aState);
        }
    }

    private showHead(aState: boolean) {
        if (this._head) {
            this._head.setVisibility(aState);
        }
    }

    private toSceneCoords(aVec: vec3): vec3 {
        return vec3.fromValues(aVec[0], aVec[2], -aVec[1]);
    }

}
